// Shared presentational helpers for the funder portal. Keeping score colors,
// pills, cards and badges here guarantees the four pages render consistently.
import React from "react";
import { GitCommitHorizontal, Flag } from "lucide-react";

// --- 42DT score dimensions (novelty / methods / impact only) -----------------
export const SCORE_DIMS = [
    { key: "novelty", label: "Novelty", color: "var(--series-blue)" },
    { key: "methodology", label: "Methods", color: "var(--series-amber)" },
    { key: "impact", label: "Impact", color: "var(--series-green)" },
];
export const SCORE_META = SCORE_DIMS.reduce((m, d) => ({ ...m, [d.key]: d }), {});
export const scoreColor = (key) => (SCORE_META[key] ? SCORE_META[key].color : "var(--brand-text)");

export const composite = (p) =>
    p ? Math.round((Number(p.novelty) + Number(p.methodology) + Number(p.impact)) / 3) : 0;

// A score value colored by where it falls (high → accent, low → amber).
export function scoreTone(v) {
    if (v >= 80) return "var(--success)";
    if (v < 65) return "var(--series-amber)";
    return "var(--ink-900)";
}

// --- Score pill / triad ------------------------------------------------------
export function ScorePill({ dimKey, value, size = "md" }) {
    const d = SCORE_META[dimKey] || { label: dimKey, color: "var(--brand-text)" };
    const fs = size === "sm" ? 11.5 : 12.5;
    return (
        <span
            className="funder-score-pill"
            title={`${d.label}: ${value}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: fs }}
        >
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
            <span style={{ color: "var(--ink-450)" }}>{d.label}</span>
            <span style={{ color: d.color, fontWeight: 700 }}>{value}</span>
        </span>
    );
}

export function ScoreTriad({ p, size = "md", gap = 16 }) {
    if (!p) return null;
    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap }}>
            {SCORE_DIMS.map((d) => (
                <ScorePill key={d.key} dimKey={d.key} value={p[d.key]} size={size} />
            ))}
        </div>
    );
}

// --- Cards -------------------------------------------------------------------
export function SectionCard({ kicker, title, subtitle, right, children, className = "", style }) {
    return (
        <div className={`funder-card ${className}`} style={style}>
            {(kicker || title || right) && (
                <div className="funder-card-header">
                    <div>
                        {kicker && <div className="funder-kicker">{kicker}</div>}
                        {title && <h2 className="funder-card-title">{title}</h2>}
                        {subtitle && <p className="funder-card-subtitle">{subtitle}</p>}
                    </div>
                    {right && <div style={{ marginLeft: "auto" }}>{right}</div>}
                </div>
            )}
            <div className="funder-card-body">{children}</div>
        </div>
    );
}

export function MetricCard({ label, value, sub, children }) {
    return (
        <div className="funder-metric-card">
            <div className="funder-kicker">{label}</div>
            {value !== undefined && <div className="funder-metric-value">{value}</div>}
            {children}
            {sub && <div className="funder-metric-sub">{sub}</div>}
        </div>
    );
}

// --- Status badge ------------------------------------------------------------
const STATUS_META = {
    Unreviewed: { bg: "var(--surface-4)", color: "var(--ink-450)" },
    "In Progress": { bg: "var(--warning-bg)", color: "var(--warning)" },
    Scored: { bg: "var(--info-bg)", color: "var(--info)" },
    Flagged: { bg: "var(--danger-bg)", color: "var(--danger)", icon: true },
    Shortlisted: { bg: "var(--success-bg)", color: "var(--success)" },
    Declined: { bg: "var(--danger-muted-bg)", color: "var(--danger-muted)" },
    Funded: { bg: "var(--success-bg)", color: "var(--success)" },
    complete: { bg: "var(--success-bg)", color: "var(--success)", text: "Complete" },
    partial: { bg: "var(--warning-bg)", color: "var(--warning)", text: "Partial" },
    needs: { bg: "var(--danger-bg)", color: "var(--danger)", text: "Needs action" },
    notstarted: { bg: "var(--surface-4)", color: "var(--ink-450)", text: "Not started" },
};

export function StatusBadge({ status }) {
    const m = STATUS_META[status] || { bg: "var(--surface-4)", color: "var(--ink-450)" };
    return (
        <span
            className="status-badge"
            style={{ background: m.bg, color: m.color, display: "inline-flex", alignItems: "center", gap: 5 }}
        >
            {m.icon && <Flag size={11} />}
            {m.text || status}
        </span>
    );
}

// --- Remit pill with hover detail -------------------------------------------
const REMIT_META = {
    in: { color: "var(--success)", label: "In scope" },
    edge: { color: "#d4a017", label: "Edge" },
    out: { color: "var(--danger-muted)", label: "Out of scope" },
};

export function RemitPill({ remit }) {
    if (!remit) return <span style={{ color: "var(--ink-250)" }}>—</span>;
    const m = REMIT_META[remit.state] || REMIT_META.in;
    const distColor = (d) => (d <= 0.3 ? "var(--success)" : d <= 0.5 ? "#d4a017" : "var(--danger-muted)");
    return (
        <span className="funder-remit">
            <span className="funder-remit-pill" style={{ borderColor: m.color, color: m.color }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.color }} />
                {m.label}
            </span>
            <span className="funder-remit-pop">
                <div className="funder-remit-row">
                    <span>Closest funded match</span>
                    <strong style={{ color: distColor(remit.fundedDistance) }}>{remit.fundedDistance.toFixed(2)}</strong>
                </div>
                {remit.fundedDescription && <div className="funder-remit-note">{remit.fundedDescription}</div>}
                <div className="funder-remit-row">
                    <span>Nearest risk cluster</span>
                    <strong style={{ color: remit.riskDistance <= 0.35 ? "var(--danger-muted)" : "var(--ink-500)" }}>
                        {remit.riskDistance.toFixed(2)} — {remit.riskCluster}
                    </strong>
                </div>
                <div className="funder-remit-divider" />
                <div className="funder-remit-explain">{remit.explanation}</div>
            </span>
        </span>
    );
}

// --- Confidence bar (proposal score card) -----------------------------------
export function ConfidenceBar({ value, band = 0, color = "var(--brand-text)", callAverage }) {
    const lo = Math.max(0, value - band);
    const hi = Math.min(100, value + band);
    return (
        <div className="funder-conf">
            <div className="funder-conf-track">
                <div className="funder-conf-band" style={{ left: `${lo}%`, width: `${hi - lo}%`, background: `color-mix(in srgb, ${color} 20%, transparent)` }} />
                <div className="funder-conf-marker" style={{ left: `${value}%`, background: color }} />
                {callAverage !== undefined && (
                    <div className="funder-conf-avg" style={{ left: `${callAverage}%` }} title={`Call average ${callAverage}`} />
                )}
            </div>
            <div className="funder-conf-labels">
                <span>Confidence {lo}–{hi}</span>
                {callAverage !== undefined && <span>Call avg {callAverage}</span>}
            </div>
        </div>
    );
}

// --- Misc --------------------------------------------------------------------
export function StageLabel({ children }) {
    return (
        <span className="funder-stage-label">
            <GitCommitHorizontal size={14} />
            {children}
        </span>
    );
}

export function CallSwitcher({ calls, callId, onChange }) {
    return (
        <label className="funder-call-switcher">
            <span className="funder-call-switcher-label">Call</span>
            <select value={callId} onChange={(e) => onChange(e.target.value)} aria-label="Active call for proposals">
                {calls.map((c) => (
                    <option key={c.id} value={c.id}>
                        {c.round}{c.status === "closed" ? " (closed)" : ""}
                    </option>
                ))}
            </select>
        </label>
    );
}

export function toneColor(tone) {
    return { green: "#2e7d32", amber: "#c5862f", red: "#c0504d", blue: "#3d6f88" }[tone] || "#666";
}
